// frontend/admin-portal/src/hooks/useDashboardData.js
import { useCallback, useEffect, useState } from "react";
import { productService } from "../services/productService";
import { orderService } from "../services/orderService";
import { treasuryService } from "../services/treasuryService";
import { purchaseOrderService } from "../services/purchaseOrderService";
import { goodsReceiptService } from "../services/goodsReceiptService";
import { rfqService } from "../services/rfqService";
import { customerService } from "../services/customerService";
import { useProducts } from "../contexts/ProductsContext";

/**
 * Fetches every piece of data the dashboard needs in parallel.
 *
 * Each slice has its own { data, loading, error } so a single failure
 * (e.g. treasury 403 for a user without permission) doesn't blank the
 * whole dashboard — the corresponding widget shows an error, the others
 * keep working.
 */
export function useDashboardData() {
    const { refresh: refreshSidebarProductsCount } = useProducts();

    const [state, setState] = useState({
        productStats: { data: null, loading: true, error: null },
        lowStock: { data: [], loading: true, error: null },
        orderStats: { data: null, loading: true, error: null },
        recentOrders: { data: [], loading: true, error: null },
        treasury: { data: null, loading: true, error: null },
        poStats: { data: null, loading: true, error: null },
        pendingRfqs: { data: [], loading: true, error: null },
        customerStats: { data: null, loading: true, error: null },
        goodsReceipts: { data: [], loading: true, error: null },
    });

    const patch = (key, value) =>
        setState((s) => ({ ...s, [key]: { ...s[key], ...value } }));

    const load = useCallback(async () => {
        // Reset all to loading
        setState((s) => {
            const next = {};
            for (const k of Object.keys(s)) {
                next[k] = { ...s[k], loading: true, error: null };
            }
            return next;
        });

        // ─── Products ────────────────────────────────────────────────
        productService
            .stats()
            .then((d) => patch("productStats", { data: d, loading: false }))
            .catch((e) =>
                patch("productStats", {
                    loading: false,
                    error: e.response?.data?.message || e.message,
                }),
            );

        productService
            .list({ lowStock: true, limit: 5, isActive: true })
            .then(({ items }) => patch("lowStock", { data: items, loading: false }))
            .catch((e) =>
                patch("lowStock", {
                    loading: false,
                    error: e.response?.data?.message || e.message,
                }),
            );

        // ─── Orders ──────────────────────────────────────────────────
        orderService
            .stats()
            .then((d) => patch("orderStats", { data: d, loading: false }))
            .catch((e) =>
                patch("orderStats", {
                    loading: false,
                    error: e.response?.data?.message || e.message,
                }),
            );

        orderService
            .list({ limit: 5, sortBy: "createdAt", sortDir: "desc" })
            .then(({ items }) => patch("recentOrders", { data: items, loading: false }))
            .catch((e) =>
                patch("recentOrders", {
                    loading: false,
                    error: e.response?.data?.message || e.message,
                }),
            );

        // ─── Treasury ────────────────────────────────────────────────
        treasuryService
            .dashboard()
            .then((d) => patch("treasury", { data: d, loading: false }))
            .catch((e) =>
                patch("treasury", {
                    loading: false,
                    error: e.response?.data?.message || e.message,
                }),
            );

        // ─── Procurement ─────────────────────────────────────────────
        purchaseOrderService
            .stats()
            .then((d) => patch("poStats", { data: d, loading: false }))
            .catch((e) =>
                patch("poStats", {
                    loading: false,
                    error: e.response?.data?.message || e.message,
                }),
            );

        rfqService
            .list({ status: "pending", limit: 5 })
            .then(({ items }) => patch("pendingRfqs", { data: items, loading: false }))
            .catch((e) =>
                patch("pendingRfqs", {
                    loading: false,
                    error: e.response?.data?.message || e.message,
                }),
            );

        goodsReceiptService
            .list({ limit: 3, status: "pending" })
            .then(({ items }) => patch("goodsReceipts", { data: items, loading: false }))
            .catch((e) =>
                patch("goodsReceipts", {
                    loading: false,
                    error: e.response?.data?.message || e.message,
                }),
            );

        // ─── Customers ───────────────────────────────────────────────
        // There's no dedicated /customers/stats endpoint yet; approximate
        // by requesting a 1-item page and reading pagination.total.
        customerService
            .list({ page: 1, limit: 1 })
            .then(({ pagination }) =>
                patch("customerStats", {
                    data: { total: pagination?.total ?? 0 },
                    loading: false,
                }),
            )
            .catch((e) =>
                patch("customerStats", {
                    loading: false,
                    error: e.response?.data?.message || e.message,
                }),
            );
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const refresh = useCallback(async () => {
        refreshSidebarProductsCount?.();
        await load();
    }, [load, refreshSidebarProductsCount]);

    return { ...state, refresh };
}